import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { PropTypes } from 'prop-types';
import { Notification, LightHeadline, StyledButton, Description } from '../../../../styledComponents/styledComponents';

import {
   troubleStartSelector,
   troubleEndSelector,
   hashSelector,
   cancelTokenSelector,
   troubleIngestStreamSelector,
   troubleTimespanManuallySetSelector,
} from '../../../../redux/selectors/selectors';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getHttpHeader } from '../../../../util/FetchTools';
import { getUnifiedErrorObject } from '../../../../util/ErrorUnifier';
import { ANALYTICS_API_URL } from '../../../../constants/config';
import PublishesTable from './PublishesTable';
import BlockSpinner from '../../../common/BlockSpinner/BlockSpinner';
import { setTroubleshootingAlertDetails } from '../../../../redux/actions/general';
import { URL_PARAMS } from '../../../../urlSync/urlParamNames';
import { INGEST_MODE } from '../../../../constants/general';
import { CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT } from '../constants';

const FETCH_ALERTS_URL = `${ANALYTICS_API_URL}/v2/alerting/ingest/find`;

function PublishesTableContainer({ created }) {
   const dispatch = useDispatch();
   const orgaHash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);

   const start = useSelector(troubleStartSelector);
   const end = useSelector(troubleEndSelector);

   const hasTimespanManuallyBeenSet = useSelector(troubleTimespanManuallySetSelector);
   const selectedStreamObj = useSelector(troubleIngestStreamSelector);

   const { ingestStream: streamName } = selectedStreamObj;

   const [duplicatedIngestAlertId, setDuplicatedIngestAlertId] = useState('');
   const [duplicatedIngestStart, setDuplicatedIngestStart] = useState();
   const [duplicatedIngestEnd, setDuplicatedIngestEnd] = useState();
   const header = useMemo(() => getHttpHeader(orgaHash), [orgaHash]);

   const { error, isLoading, isFetching } = useQuery({
      queryKey: ['preDuplicatedIngestCheck', start.format(), end.format(), streamName],
      enabled: !hasTimespanManuallyBeenSet,
      queryFn: () => {
         return axios
            .get(FETCH_ALERTS_URL, {
               headers: header,
               cancelToken: cancelToken.token,
               params: {
                  streams: streamName,
                  from: moment.utc(start).format(),
                  to: moment.utc(end).format(),
                  details: false,
               },
            })
            .then((res) => res.data);
      },
      onSuccess: (res) => {
         if (res.data.length > 0) {
            const duplicatedIngestAlerts = res.data.filter((alert) => alert.type === 'duplicatedIngest');
            const foundAlertsCount = duplicatedIngestAlerts.length;
            if (foundAlertsCount > 0) {
               const latestAlertId = duplicatedIngestAlerts[foundAlertsCount - 1].id;
               const endMoment = moment.utc(duplicatedIngestAlerts[foundAlertsCount - 1].last.asUnixInMS);
               const startMoment = moment.utc(duplicatedIngestAlerts[0].first.asUnixInMS);
               setDuplicatedIngestAlertId(latestAlertId);
               setDuplicatedIngestStart(startMoment);
               setDuplicatedIngestEnd(endMoment);
            }
         }
      },
      refetchOnWindowFocus: false,
   });

   const processedError = error ? getUnifiedErrorObject(error) : error;

   return (
      <>
         {isLoading || isFetching ? (
            <BlockSpinner paddingTopBottomPx={50} />
         ) : (
            <>
               {processedError ? (
                  <Notification indentPx={0}>
                     {processedError.message ? processedError.message : processedError}
                  </Notification>
               ) : duplicatedIngestAlertId ? (
                  <>
                     <LightHeadline
                        marginBottomPx={5}
                        marginTopPx={8}
                     >{`Detected a problem with stream '${streamName}', duplicated ingests were found.`}</LightHeadline>
                     {duplicatedIngestStart && duplicatedIngestEnd ? (
                        <Description
                           marginLeftPx={0}
                           $fontSizePx={13}
                           marginBottomPx={20}
                        >{`Occurrence of duplicated ingests: ${duplicatedIngestStart.format(
                           CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT,
                        )} - ${duplicatedIngestEnd?.format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT)}`}</Description>
                     ) : (
                        <></>
                     )}
                     <StyledButton
                        type='button'
                        fontSizePx={11}
                        className={'btn'}
                        fixMargin
                        name={'duplicatedIngestBtn'}
                        id={'duplicatedIngestBtn'}
                        heightPx={22}
                        widthPx={120}
                        bgColor={'rgb(150,150,150)'}
                        fontColor={'#FFF'}
                        bgColorHover={'rgb(130,130,130)'}
                        fontColorHover={'#FFF'}
                        onClick={() => {
                           const payload = {
                              alertId: duplicatedIngestAlertId,
                              troubleIngestMode: INGEST_MODE.ALERT,
                           };
                           dispatch(setTroubleshootingAlertDetails(payload, {
                              [URL_PARAMS.ALERT_ID]: duplicatedIngestAlertId,
                              [URL_PARAMS.TROUBLE_INGEST_MODE]: INGEST_MODE.ALERT,
                           }));
                        }}
                     >
                        {'See more details'}
                     </StyledButton>
                  </>
               ) : (
                  <PublishesTable created={created} />
               )}
            </>
         )}
      </>
   );
}

PublishesTableContainer.propTypes = {
   created: PropTypes.string,
};

export default PublishesTableContainer;
